import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  People as PeopleIcon,
  Person as PersonIcon,
  VideoCall as VideoCallIcon,
  MeetingRoom as MeetingRoomIcon,
} from '@mui/icons-material';
import { dashboardAPI } from '../services/api';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const StatCard = ({ title, value, icon: Icon, color, onClick }) => {
  const theme = useTheme();
  return (
    <Paper
      onClick={onClick}
      sx={{
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        height: 140,
        backgroundColor: theme.palette.mode === 'dark' ? '#242424' : '#ffffff',
        boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': {
          boxShadow: onClick ? '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)' : '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
        },
      }}
    >
      <Box display="flex" alignItems="center" mb={2}>
        <Box
          sx={{
            p: 1,
            borderRadius: 1,
            backgroundColor: `${color}20`,
            color: color,
            mr: 2,
          }}
        >
          <Icon />
        </Box>
        <Typography variant="h6" color="text.secondary">
          {title}
        </Typography>
      </Box>
      <Typography variant="h4" component="div" sx={{ flexGrow: 1 }}>
        {value}
      </Typography>
    </Paper>
  );
};

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalMeetings: 0,
    totalRooms: 0,
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      const response = await dashboardAPI.getStatistics();
      console.log('Dashboard API Response:', response.data);
      
      if (response.data.success && response.data.data) {
        setStatistics(response.data.data);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Dashboard API Error:', error);
      const errorMessage = error.response?.data?.message || 
                         error.message || 
                         'Failed to fetch statistics';
      toast.error(errorMessage, {
        id: 'dashboard-fetch-error'
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={statistics.totalUsers}
            icon={PeopleIcon}
            color="#6366f1"
            onClick={() => navigate('/admin/users')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Users"
            value={statistics.activeUsers}
            icon={PersonIcon}
            color="#10b981"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Meetings"
            value={statistics.totalMeetings}
            icon={VideoCallIcon}
            color="#f59e0b"
            onClick={() => navigate('/admin/meetings')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Rooms"
            value={statistics.totalRooms}
            icon={MeetingRoomIcon}
            color="#8b5cf6"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard; 